import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import WikiEditor from './WikiEditor';
import { createWikiPage, getWikiPageById, updateWikiPage } from '../../../services/wikiServices/WikiPageService';
import { useAuth } from '../../../firebase/AuthProvider';
import { WikiPage, NewWikiPageRequest, UpdateWikiPageRequest } from '../../../interfaces/wiki/Wiki';
import { useNotifications } from '../../../contexts/NotificationProvider';
import TopToolbar from './TopToolbar';
import BubbleMenuComponent from './BubbleMenuComponent';
import FloatingMenuComponent from './FloatingMenuComponent';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import CodeBlock from '@tiptap/extension-code-block';
import { Admonition } from './extensions/Admonition';
import { SlashCommandExtension } from './extensions/SlashCommandExtension';
import { InternalLink } from './extensions/InternalLinkExtension';
import { InternalLinkSuggestionExtension } from './extensions/InternalLinkSuggestionExtension';
import { Mention } from './extensions/MentionExtension';
import { MentionSuggestionExtension } from './extensions/MentionSuggestionExtension';
import { ImageUpload } from './extensions/ImageUploadExtension';
import { prepareFileUpload, uploadFileToAzure } from '../../../services/wikiServices/WikiFileService';
import './WikiEditor.css'; // Import the new stylesheet
import { FileUploadPrepareRequest } from '../../../interfaces/file/FileUpload';
import { Button } from 'react-bootstrap';

import WikiSidebar from './WikiSidebar';

const WikiEditorWrapper: React.FC = () => {
  const { spaceId, pageId } = useParams<{ spaceId: string; pageId?: string }>();
  const { currentUser } = useAuth();
  const [wikiContent, setWikiContent] = useState<string | undefined>(undefined);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<WikiPage | null>(null);
  const [isEditing, setIsEditing] = useState(false); // State for edit mode
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { notify } = useNotifications();

  const handleImageUpload = async (file: File): Promise<string | null> => {
    const token = currentUser ? await currentUser.getIdToken() : null;
    if (!token) {
      notify('Authentication token is required for file upload.', { type: 'error' });
      return null;
    }
    // ... (rest of the function is unchanged)
    try {
      const fileMetadata: FileUploadPrepareRequest = {
        fileName: file.name,
        contentType: file.type,
        contentLength: file.size,
      };
      const prepareResponse = await prepareFileUpload(token, fileMetadata);
      if (!prepareResponse) {
        notify('Failed to prepare file upload.', { type: 'error' });
        return null;
      }
      const { uploadUrl, finalUrl } = prepareResponse;
      const uploadSuccess = await uploadFileToAzure(uploadUrl, file, file.type);
      if (!uploadSuccess) {
        notify('Failed to upload file to Azure.', { type: 'error' });
        return null;
      }
      return finalUrl;
    } catch (err) {
      console.error('Image upload failed:', err);
      notify('An error occurred during image upload.', { type: 'error' });
      return null;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, codeBlock: false }),
      Placeholder.configure({ placeholder: 'Start writing here...' }),
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CodeBlock,
      Admonition,
      ImageUpload.configure({
        uploadFile: (file) => handleImageUpload(file),
      }),
      SlashCommandExtension,
      InternalLink,
      InternalLinkSuggestionExtension,
      Mention,
      MentionSuggestionExtension,
    ],
    content: wikiContent,
    editable: false, // Start in non-editable mode
    editorProps: {
      attributes: {
        class: 'ProseMirror',
        'data-auth-token': '',
      },
    },
  });

  // Toggle editable state when isEditing changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditing);
    }
  }, [isEditing, editor]);

  // Fetch and authentication logic...
  useEffect(() => {
    const setAuthToken = async () => {
      if (editor && currentUser) {
        const token = await currentUser.getIdToken();
        editor.setOptions({
          editorProps: {
            attributes: {
              'data-auth-token': token,
            },
          },
        });
      } else if (editor && !currentUser) {
        editor.setOptions({
          editorProps: {
            attributes: {
              'data-auth-token': '',
            },
          },
        });
      }
    };
    setAuthToken();
  }, [editor, currentUser]);

  useEffect(() => {
    const fetchPageContent = async () => {
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (spaceId && pageId && token) {
        setLoading(true);
        try {
          const page = await getWikiPageById(token, pageId);
          if (page) {
            setPageData(page);
            setWikiContent(JSON.stringify(page.content));
            setPageTitle(page.title);
            editor?.commands.setContent(page.content);
          } else {
            notify('Wiki page not found.', { type: 'error' });
          }
        } catch (err) {
          notify('Failed to load wiki page.', { type: 'error' });
        } finally {
          setLoading(false);
        }
      } else if (spaceId) {
        setWikiContent('');
        setPageTitle('Untitled Page');
        setPageData(null);
        setIsEditing(true); // Start new pages in edit mode
        setLoading(false);
      } else {
        notify('No space selected.', { type: 'error' });
        setLoading(false);
      }
    };
    fetchPageContent();
  }, [spaceId, pageId, currentUser, editor, notify]);

  const handleSave = useCallback(async () => {
    // ... (validation logic is unchanged)
    const token = currentUser ? await currentUser.getIdToken() : null;
    if (!token || !editor || !pageTitle || !spaceId) {
      notify('Cannot save: missing required information.', { type: 'warning' });
      return;
    }

    setIsSaving(true);
    const jsonContent = editor.getJSON();

    try {
      if (pageId) {
        const updatedPage = await updateWikiPage(token, pageId, { title: pageTitle, content: jsonContent });
        if (updatedPage) {
          setPageData(updatedPage);
          notify('Page updated successfully!', { type: 'success' });
          setIsEditing(false); // Switch to view mode
        }
      } else {
        const newPage = await createWikiPage(token, spaceId, { spaceId, title: pageTitle, content: jsonContent });
        if (newPage) {
          setPageData(newPage);
          notify('Page created successfully!', { type: 'success' });
          setIsEditing(false); // Switch to view mode
          // navigate(`/wiki/spaces/${spaceId}/${newPage.id}`);
        }
      }
    } catch (err) {
      notify('Failed to save page.', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [currentUser, spaceId, pageId, pageTitle, editor, notify]);
  
  const handleCancel = () => {
    if (pageData) {
      // Revert changes
      editor?.commands.setContent(pageData.content);
      setPageTitle(pageData.title);
    }
    setIsEditing(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="container-fluid wiki-page-layout">
      <WikiSidebar />
      <div className="wiki-editor-wrapper">
        <div className="wiki-header">
          <div className="wiki-title-area">
            {isEditing ? (
              <input
                type="text"
                className="wiki-title-input"
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
                placeholder="Page Title"
              />
            ) : (
              <h1 className="wiki-title-display">{pageTitle || 'Untitled Page'}</h1>
            )}
            <div className="wiki-controls">
              {isEditing ? (
                <>
                  <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Publishing...' : 'Publish'}
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
            </div>
          </div>
          {pageData && (
            <div className="wiki-metadata">
              <span>Created on {new Date(pageData.createdAt).toLocaleDateString()}</span>
              <span>Last updated on {new Date(pageData.updatedAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>
        {isEditing && (
          <div className="toolbar-container">
            <TopToolbar editor={editor} />
            <BubbleMenuComponent editor={editor} />
            <FloatingMenuComponent editor={editor} />
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};




export default WikiEditorWrapper;
